import { CodeBlock } from '@ui/pages/onboarding/components/configuration/CodeBlock'

import vercelEnvVarsImage from './assets/vercel-env-vars.png'
import { DATABLE_API_KEY } from './constants'
import { ConfigurationProps } from './types'

export const VercelConfiguration = ({ instanceUrl, datableApiKey }: ConfigurationProps) => (
  <>
    <h1 className="my-4 text-3xl font-bold">Vercel</h1>

    <p className="my-4 text-gray-500">
      Get started quickly by instrumenting your app with Vercel's OpenTelemetry wrapper. You can also custom
      instrumenting your app for more flexibility.
    </p>

    <p className="my-4 text-gray-500">
      Sending telemetry directly from your app to a vendor is not recommended for high throughput or single threaded
      apps. We recommend hosting a local OpenTelemetry collectory to buffer and forward telemetry.
    </p>

    <h2 className="my-4 text-2xl font-bold">Send traces with Vercel's automatic instrumentation</h2>

    <p className="my-4 text-gray-500">
      This is the fastest way to send OpenTelemetry traces from a Vercel app. There are two mechanisms - either with
      environment variables, or with a configuration file. ENV VARs is the fastest but least flexible.
    </p>

    <p className="my-4 text-gray-500">
      Set up an <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">instrumentation.ts</code> file. The{' '}
      <a
        href="https://nextjs.org/docs/app/building-your-application/optimizing/open-telemetry#getting-started"
        className="text-blue-500 underline hover:text-blue-700"
        target="_blank">
        next.js open telemetry
      </a>{' '}
      page has additional configuration options.
    </p>

    <h3 className="my-4 text-xl font-bold">[Fastest path] Sending via ENV VARS</h3>

    <p className="my-4 text-gray-500">
      <strong>
        1. Set{' '}
        <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">experimental.instrumentationHook = true</code>{' '}
        in your <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">next.config.js</code> file:
      </strong>
    </p>

    <CodeBlock
      code={`
// next.config.js
module.exports = {
  experimental: {
    instrumentationHook: true,
  },
};
`}
      language="typescript"
    />

    <p className="my-4 text-gray-500">
      <strong>2. Install dependencies.</strong>
    </p>

    <CodeBlock
      code={`
npm i --save @vercel/otel
`}
      language="bash"
    />

    <p className="my-4 text-gray-500">
      <strong>
        3. Create a <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">instrumentation.ts</code> file in
        your root (or <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">src</code>) directory:
      </strong>
    </p>

    <CodeBlock
      code={`
// instrumentation.ts
import { registerOTel } from "@vercel/otel";

export function register() {
  registerOTel("your-service-name");
}
`}
      language="typescript"
    />

    <p className="my-4 text-gray-500">
      <strong>4. Configure ENV VARS inside of Vercel settings to send to Datable</strong>
    </p>

    <p className="my-4 text-gray-500">
      Go to{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">
        https://vercel.com/&lt;team&gt;/&lt;project&gt;/settings/environment-variables
      </code>{' '}
      and set the following:
    </p>

    <CodeBlock
      code={`
OTEL_EXPORTER_OTLP_PROTOCOL=http/protobuf
OTEL_EXPORTER_OTLP_ENDPOINT=https://${instanceUrl}:4318
`}
    />

    <p className="my-4 text-gray-500">
      Which should look like this: <br />
      <img src={vercelEnvVarsImage} alt="Vercel Env Vars" />
    </p>

    <p className="my-4 text-gray-500">
      <strong>5. Deploy your app to Vercel, and you should see traces in Datable. 🎉</strong>
    </p>

    <h3 className="my-4 text-xl font-bold">[optional] Additional ENV VARS options for more control</h3>

    <p className="my-4 text-gray-500">
      Alternatively, instead of{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">OTEL_EXPORTER_OTLP_PROTOCOL</code> and{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">OTEL_EXPORTER_OTLP_ENDPOINT</code> from step 4,
      you can specify the trace protocol and endpoint. You would do this to send logs, metrics, and traces to different
      destinations.
    </p>

    <p className="my-4 text-gray-500">
      Only use one. We recommend <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">http/protobuf</code>{' '}
      for backend, and <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">http/json</code> for web
      applications.
    </p>

    <CodeBlock
      code={`
OTEL_EXPORTER_OTLP_TRACES_PROTOCOL=http/protobuf
OTEL_EXPORTER_OTLP_TRACES_ENDPOINT=https://${instanceUrl}:4318/v1/traces
`}
    />

    <CodeBlock
      code={`
OTEL_EXPORTER_OTLP_TRACES_PROTOCOL=http/json
OTEL_EXPORTER_OTLP_TRACES_ENDPOINT=https://${instanceUrl}:4318/v1/traces
`}
    />

    <CodeBlock
      code={`
OTEL_EXPORTER_OTLP_TRACES_PROTOCOL=grpc
OTEL_EXPORTER_OTLP_TRACES_ENDPOINT=https://${instanceUrl}:4317
`}
    />

    <p className="my-4 text-gray-500">
      <a
        href="https://opentelemetry.io/docs/languages/sdk-configuration/otlp-exporter/"
        className="text-blue-500 underline hover:text-blue-700"
        target="_blank">
        Defaults and additional options can be found on the OTel exporter page
      </a>
    </p>

    <h3 className="my-4 text-xl font-bold">Sending via configuration</h3>

    <p className="my-4 text-gray-500">
      If you need more control over sending your telemetry, or just don't like the magic of setting environment
      variables, you can define your own exporter. This is effectively the same as the ENV VAR method but explicit in
      its settings.
    </p>

    <p className="my-4 text-gray-500">
      <strong>
        1. Set{' '}
        <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">experimental.instrumentationHook = true</code>{' '}
        in your <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">next.config.js</code> file.
      </strong>
    </p>

    <CodeBlock
      code={`
// next.config.js
module.exports = {
  experimental: {
    instrumentationHook: true,
  },
};
`}
      language="typescript"
    />

    <p className="my-4 text-gray-500">
      <strong>2. Install dependencies.</strong>
    </p>

    <CodeBlock
      code={`
npm i --save @opentelemetry/exporter-trace-otlp-proto \\
@vercel/otel
`}
      language="bash"
    />

    <p className="my-4 text-gray-500">
      <strong>
        3. Create a <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">instrumentation.ts</code> file in
        your root (or <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">src</code>) directory.
      </strong>
    </p>

    <CodeBlock
      code={`
// instrumentation.ts
import { registerOTel } from "@vercel/otel";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-proto";

export function register() {

  const spanCollectorOptions = {
    url: "https://${instanceUrl}:4318/v1/traces",
    headers: {
      "${DATABLE_API_KEY}": ${datableApiKey}
    }
  };

  registerOTel({
    serviceName: "your-project-name",
    traceExporter: new OTLPTraceExporter(spanCollectorOptions),
  });
}
`}
      language="typescript"
    />

    <p className="my-4 text-gray-500">
      <strong>4. Deploy your app to Vercel, and you should see traces in Datable. 🎉</strong>
    </p>

    <h2 className="my-4 text-2xl font-bold">Sending traces with custom instrumentation</h2>

    <p className="my-4 text-gray-500">This path gives you the most control, but is also the most complex.</p>

    <p className="my-4 text-gray-500">
      The open source OpenTelemetry libraries{' '}
      <a
        href="https://nextjs.org/docs/app/building-your-application/optimizing/open-telemetry#manual-opentelemetry-configuration"
        className="text-blue-500 underline hover:text-blue-700"
        target="_blank">
        are not compatible with the edge runtime
      </a>
      , so it's recommended to use the{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">@vercel/otel</code> package to send traces to
      Datable. If you do choose to use custom instrumentation with the vanilla OpenTelemetry libraries, we recommend
      adding a check to your <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">instrumentation.ts</code>{' '}
      file to ensure it only runs in the Node.js runtime.
    </p>
    <p className="my-4 text-gray-500">
      <strong>
        1. Enable the instrumentation hook in your{' '}
        <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">next.config.js</code> file.
      </strong>
    </p>

    <CodeBlock
      code={`
// next.config.js
module.exports = {
  experimental: {
    instrumentationHook: true,
  },
};
`}
      language="typescript"
    />

    <p className="my-4 text-gray-500">
      <strong>
        2. Create a <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">instrumentation.ts</code> file in
        your root (or <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">src</code>) directory.
      </strong>
    </p>

    <CodeBlock
      code={`
// instrumentation.ts
export async function register() {
  if (process.env.NEXT_RUNTIME === "nodejs") {
    await import("./tracer");
  }
}
`}
      language="typescript"
    />

    <p className="my-4 text-gray-500">
      <strong>3. Install dependencies</strong>
    </p>

    <CodeBlock
      code={`
npm i --save @opentelemetry/sdk-node \\
@opentelemetry/exporter-trace-otlp-proto \\
@opentelemetry/resources \\
@opentelemetry/semantic-conventions \\
@opentelemetry/sdk-trace-node
`}
      language="bash"
    />

    <p className="my-4 text-gray-500">
      <strong>
        4. Add the tracing library to your project. This will batch traces, and send them directly to Datable.
      </strong>
    </p>

    <CodeBlock
      code={`
// tracer.ts
import { NodeSDK } from "@opentelemetry/sdk-node";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-proto";
import { Resource } from "@opentelemetry/resources";
import { SemanticResourceAttributes } from "@opentelemetry/semantic-conventions";
import { BatchSpanProcessor } from "@opentelemetry/sdk-trace-node";

const spanCollectorOptions = {
  url: "https://${instanceUrl}:4318/v1/traces",
  headers: {
    "${DATABLE_API_KEY}": ${datableApiKey}
  }
};

const traceExporter = new OTLPTraceExporter(spanCollectorOptions);

const sdk = new NodeSDK({
  resource: new Resource({
    [SemanticResourceAttributes.SERVICE_NAME]: "next-app",
  }),
  spanProcessor: new BatchSpanProcessor(traceExporter),
});
sdk.start();
`}
      language="typescript"
    />

    <p className="my-4 text-gray-500">
      NextJS auto instruments itself, but for additional configurations{' '}
      <a
        href="https://nextjs.org/docs/app/building-your-application/optimizing/open-telemetry#custom-spans"
        className="text-blue-500 underline hover:text-blue-700"
        target="_blank">
        see the nextjs examples
      </a>
    </p>

    <p className="my-4 text-gray-500">
      <strong>5. Deploy your app to Vercel, and you should see traces in Datable. 🎉</strong>
    </p>

    <h2 className="my-4 text-2xl font-bold">[Untested] Sending logs</h2>

    <p className="my-4 text-gray-500">🔥 Here be dragons 🔥</p>

    <p className="my-4 text-gray-500">
      The OpenTelemetry Javascript SDK log agent support is{' '}
      <a
        href="https://github.com/open-telemetry/opentelemetry-js?tab=readme-ov-file#feature-status"
        className="text-blue-500 underline hover:text-blue-700"
        target="_blank">
        not yet stable
      </a>
      , and the following is untested in Vercel.
    </p>

    <p className="my-4 text-gray-500">
      It's worth looking at the differences between sending logs for the web versus from nodejs in the{' '}
      <a
        href="https://www.npmjs.com/package/@opentelemetry/exporter-trace-otlp-http"
        className="text-blue-500 underline hover:text-blue-700"
        target="_blank">
        Otel Log Exporter documentation
      </a>
      .
    </p>

    <p className="my-4 text-gray-500">
      As with traces, it's not recommended to send logs directly from your app to a vendor. We recommend hosting a local
      OpenTelemetry Collector or a log forwarder like FluentBit or Vector to collect, buffer, and forward logs.
    </p>

    <p className="my-4 text-gray-500">That said, here's how you might send logs from your Vercel app to Datable:</p>

    <h3 className="my-4 text-xl font-bold">Winston</h3>

    <p className="my-4 text-gray-500">
      <strong>1. Install the log dependencies</strong>
    </p>

    <CodeBlock
      code={`
npm i --save @opentelemetry/exporter-logs-otlp-http \\
@opentelemetry/sdk-logs \\
@opentelemetry/resources \\
@opentelemetry/semantic-conventions \\
@opentelemetry/api-logs
`}
      language="bash"
    />

    <p className="my-4 text-gray-500">
      <strong>
        2. Add a <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">logger.ts</code> file to your project
      </strong>
    </p>

    <CodeBlock
      code={`
// logger.ts
import {
  LoggerProvider,
  BatchLogRecordProcessor,
} from "@opentelemetry/sdk-logs";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { SemanticResourceAttributes } from "@opentelemetry/semantic-conventions";
import { SeverityNumber } from "@opentelemetry/api-logs";
import { Resource } from "@opentelemetry/resources";

import { createLogger, transports } from "winston";

const resource = new Resource({
  [SemanticResourceAttributes.SERVICE_NAME]: "my app",
  [SemanticResourceAttributes.SERVICE_VERSION]: "0.0.1",
});

const logCollectorOptions = {
  url: "https://${instanceUrl}:4318/v1/logs",
  headers: {
    "${DATABLE_API_KEY}": ${datableApiKey}
  }
};
const logExporter = new OTLPLogExporter(logCollectorOptions);
const loggerProvider = new LoggerProvider({ resource });

loggerProvider.addLogRecordProcessor(new BatchLogRecordProcessor(logExporter));

const otelLogger = loggerProvider.getLogger("otel", "1.0.0");

// Adding Winston support
// Replace with your log framework, or use otelLogger.emit() directly
const formatLog = (args: any) => {
  typeof args === "string" ? args : JSON.stringify(args);
};

// your winston implementation
const consoleTransport = new transports.Console();
const logger = createLogger({
  transports: [consoleTransport],
});

const customLogger = {
  ...logger,
  info: (args: any) => {
    otelLogger.emit({
      body: formatLog(args),
      severityNumber: SeverityNumber.INFO,
    });
    return logger.info(args);
  },

  error: (args: any) => {
    otelLogger.emit({
      body: formatLog(args),
      severityNumber: SeverityNumber.ERROR,
    });
    return logger.error(args);
  },
};

export default customLogger;
`}
      language="typescript"
    />

    <p className="my-4 text-gray-500">
      <strong>3. Register the logger</strong>
    </p>

    <CodeBlock
      code={`
// instrumentation.ts
export async function register() {
  if (process.env.NEXT_RUNTIME === "nodejs") {
    await import("./tracer");

    // Does this need to be in the nodejs runtime only?
    // Can it run on Edge and the Web?
    await import("./logger");
  }
}
`}
      language="typescript"
    />

    <p className="my-4 text-gray-500">
      <strong>4. Use the logger inside your app</strong>
      <br />
      This assumes invocation inside a nodejs runtime.
    </p>

    <CodeBlock
      code={`

// myHelloWorld.ts
import logger from "./logger";

logger.info("Hello, world!");
`}
      language="typescript"
    />

    <p className="my-4 text-gray-500">
      <strong>5. Deploy your app to Vercel, and you should see logs in Datable. 🎉</strong>
    </p>

    <h3 className="my-4 text-xl font-bold">Pino</h3>

    <p className="my-4 text-gray-500">
      <strong>1. Install dependencies</strong>
    </p>

    <CodeBlock
      code={`
npm i @opentelemetry/exporter-logs-otlp-http \\
@opentelemetry/sdk-logs \\
@opentelemetry/resources \\
@opentelemetry/semantic-conventions \\
@opentelemetry/api-logs
`}
      language="bash"
    />

    <p className="my-4 text-gray-500">
      <strong>
        2. Add a <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">logger.ts</code> file to your project
      </strong>
    </p>

    <CodeBlock
      code={`
// logger.ts
import {
  LoggerProvider,
  BatchLogRecordProcessor,
} from "@opentelemetry/sdk-logs";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { SemanticResourceAttributes } from "@opentelemetry/semantic-conventions";
import { SeverityNumber } from "@opentelemetry/api-logs";
import { Resource } from "@opentelemetry/resources";

import { createLogger, transports } from "winston";

const resource = new Resource({
  [SemanticResourceAttributes.SERVICE_NAME]: "my app",
  [SemanticResourceAttributes.SERVICE_VERSION]: "0.0.1",
});

const logCollectorOptions = {
  url: "https://${instanceUrl}:4318/v1/logs",
  headers: {
    "${DATABLE_API_KEY}": ${datableApiKey}
  }
};
const logExporter = new OTLPLogExporter(logCollectorOptions);
const loggerProvider = new LoggerProvider({ resource });

loggerProvider.addLogRecordProcessor(new BatchLogRecordProcessor(logExporter));

const otelLogger = loggerProvider.getLogger("otel", "1.0.0");

const pinoLogger = pino();

const formatLog = (args: any) =>
  typeof args === "string" ? args : JSON.stringify(args);

export const customLogger = {
  info: (args: any) => {
    otelLogger.emit({
      body: formatLog(args),
      severityNumber: SeverityNumber.INFO,
    });
    pinoLogger.info(args);
  },

  error: (args: any) => {
    otelLogger.emit({
      body: formatLog(args),
      severityNumber: SeverityNumber.ERROR,
    });
    pinoLogger.error(args);
  },
};
`}
      language="typescript"
    />

    <p className="my-4 text-gray-500">
      <strong>3. Register the logger</strong>
    </p>

    <CodeBlock
      code={`
// instrumentation.ts
export async function register() {
  if (process.env.NEXT_RUNTIME === "nodejs") {
    await import("./tracer");
    // Does this need to be in the nodejs runtime only?
    // Can it run on Edge and the Web?
    await import("./logger");
  }
}
`}
      language="typescript"
    />

    <p className="my-4 text-gray-500">
      <strong>4. Use the logger</strong>
      <br />
      This assumes invocation inside a nodejs runtime.
    </p>

    <CodeBlock
      code={`
// myHelloWorld.ts
import logger from "./logger";

logger.info("Hello, world!");
`}
      language="typescript"
    />

    <p className="my-4 text-gray-500">
      <strong>5. Deploy your app to Vercel, and you should see logs in Datable. 🎉</strong>
    </p>
  </>
)
